import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { finalize } from 'rxjs';
import { AngularMaterialModule } from 'src/app/material.module';
import { BaseComponent } from '@lib/components';
import { ApiResponseError } from '@lib/interfaces';
import { getNeoCashOutFinalCashAmount, getNeoCashOutServiceFee, getNeoCashOutWalletCredit, INeoCashOutRedeemResponse, INeoCashOutVoucherDetails, NEO_CASH_OUT_PANEL_MIN_WIDTH_PX, NeoCashOutOperationCode } from '@lib/interfaces/card-less.interface';
import { CardLessService } from '@lib/services/card-less/card-less.service';
import { SidePanelService } from '@lib/services';
import { ApproveConfirmModalComponent } from '../approve-confirm-modal/approve-confirm-modal.component';
import { DeclineConfirmModalComponent, IDeclineResult } from '../decline-confirm-modal/decline-confirm-modal.component';
import { CashOutResultComponent } from '../cash-out-result/cash-out-result.component';

// NEOIQ-2700: no threshold was specified by Figma (the static mockup only shows one healthy state)
// or by backend, so the last-5-minutes warning window is a product choice made for this build —
// easy to retune if a different window is requested later.
const URGENCY_THRESHOLD_MS = 5 * 60 * 1000;

@Component({
    selector: 'app-voucher-details',
    standalone: true,
    templateUrl: './voucher-details.component.html',
    styleUrls: ['./voucher-details.component.scss'],
    imports: [AngularMaterialModule],
})
export class VoucherDetailsComponent extends BaseComponent implements OnInit, OnDestroy {
    voucherDetails: INeoCashOutVoucherDetails;
    agentAccountId: string;

    protected loading = false;
    protected countdown = '';
    protected progressPercent = 100;
    protected isUrgent = false;
    private timerRef: ReturnType<typeof setInterval>;

    constructor(
        private readonly dialogRef: MatDialogRef<VoucherDetailsComponent>,
        private readonly sidePanel: SidePanelService,
        private readonly dialog: MatDialog,
        private readonly cardLessService: CardLessService,
        private readonly snackbar: MatSnackBar,
    ) {
        super();
    }

    ngOnInit(): void {
        this.updateCountdown();
        this.timerRef = setInterval(() => this.updateCountdown(), 1000);
    }

    override ngOnDestroy(): void {
        clearInterval(this.timerRef);
        super.ngOnDestroy();
    }

    protected get customerInitials(): string {
        const { first_name, last_name } = this.voucherDetails.user;
        return `${first_name?.charAt(0) ?? ''}${last_name?.charAt(0) ?? ''}`.toUpperCase();
    }

    protected get customerFullName(): string {
        return `${this.voucherDetails.user.first_name} ${this.voucherDetails.user.last_name}`.trim();
    }

    protected get serviceFee(): number {
        return getNeoCashOutServiceFee(this.voucherDetails);
    }

    protected get finalCashAmount(): number {
        return getNeoCashOutFinalCashAmount(this.voucherDetails);
    }

    protected get walletCredit(): number {
        return getNeoCashOutWalletCredit(this.voucherDetails);
    }

    private updateCountdown(): void {
        const totalMs = new Date(this.voucherDetails.expiry_time).getTime() - new Date(this.voucherDetails.issued_on).getTime();
        const distance = new Date(this.voucherDetails.expiry_time).getTime() - Date.now();

        this.isUrgent = distance <= URGENCY_THRESHOLD_MS;

        if (distance <= 0) {
            this.countdown = '00 : 00 : 00';
            this.progressPercent = 0;
            clearInterval(this.timerRef);
            return;
        }

        this.progressPercent = totalMs > 0 ? Math.min(100, Math.max(0, (distance / totalMs) * 100)) : 0;

        const pad = (value: number) => value.toString().padStart(2, '0');
        const hours = Math.floor(distance / (1000 * 60 * 60));
        const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((distance % (1000 * 60)) / 1000);

        this.countdown = `${pad(hours)} : ${pad(minutes)} : ${pad(seconds)}`;
    }

    protected openApprove(): void {
        this.addSubscription(
            this.dialog
                .open(ApproveConfirmModalComponent, {
                    width: '548px',
                    data: { voucherDetails: this.voucherDetails },
                })
                .afterClosed()
                .subscribe((approved: boolean) => {
                    if (approved) this.redeem();
                }),
        );
    }

    protected openDecline(): void {
        this.addSubscription(
            this.dialog
                .open(DeclineConfirmModalComponent, {
                    width: '548px',
                })
                .afterClosed()
                .subscribe((result?: IDeclineResult) => {
                    if (!result?.declined) return;

                    // Decline has no backend call to wait on (NEOIQ-2701 — declining is local-only,
                    // see decline-confirm-modal.component.ts), but the loading overlay still gives
                    // the agent a clear "this is happening" signal for the brief panel-swap to the
                    // result screen, consistent with Approve/Continue/Download Receipt.
                    this.loading = true;
                    this.dialogRef.close();
                    const resultDialogRef = this.sidePanel.open(CashOutResultComponent, {}, NEO_CASH_OUT_PANEL_MIN_WIDTH_PX);
                    resultDialogRef.componentInstance.voucherDetails = this.voucherDetails;
                    resultDialogRef.componentInstance.status = 'DECLINED';
                }),
        );
    }

    private redeem(): void {
        this.loading = true;

        const idempotencyKey = crypto.randomUUID();
        this.addSubscription(
            this.cardLessService
                .neoCashOutRedeem(
                    this.voucherDetails.voucher_code,
                    {
                        operation_code: NeoCashOutOperationCode.CASH_OUT,
                        agent_account_id: this.agentAccountId,
                        amount: this.voucherDetails.amount,
                    },
                    idempotencyKey,
                )
                .pipe(finalize(() => (this.loading = false)))
                .subscribe({
                    next: (redeemResponse: INeoCashOutRedeemResponse) => {
                        this.dialogRef.close();
                        // The redeem response is a much smaller shape than the lookup response (no
                        // voucher_code/user/reference_number/commission_amount) — merge its
                        // authoritative final amount/fee/currency/status onto the already-fetched
                        // lookup details rather than passing it through directly.
                        const redeemedDetails: INeoCashOutVoucherDetails = {
                            ...this.voucherDetails,
                            account_id: redeemResponse.account_id,
                            currency: redeemResponse.currency,
                            amount: redeemResponse.amount,
                            fee_amount: redeemResponse.fee_amount,
                            voucher_status: redeemResponse.status,
                        };
                        const resultDialogRef = this.sidePanel.open(CashOutResultComponent, {}, NEO_CASH_OUT_PANEL_MIN_WIDTH_PX);
                        resultDialogRef.componentInstance.voucherDetails = redeemedDetails;
                        resultDialogRef.componentInstance.status = 'REDEEMED';
                    },
                    error: (error: ApiResponseError) => this.createApiErrorSnackBar(this.snackbar, error),
                }),
        );
    }

    protected back(): void {
        this.dialogRef.close();
    }
}
